import { useEffect, useState, useCallback } from "react";
import { api, notifyApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Lightbulb, Zap, Power } from "lucide-react";

/**
 * Platform-scoped Table Light Wiring panel.
 * Placed inside the Platform Console (below Hardware Automation) so the
 * platform owner centrally manages wiring for every cafe.
 *
 * Props:
 *   cafeId — the cafe id whose tables should be wired
 *   enabled — whether the cafe has automation_enabled = true
 */
export default function PlatformLightWiringPanel({ cafeId, enabled }) {
  const [tables, setTables] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [wireSaving, setWireSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [wiring, setWiring] = useState({});

  const refresh = useCallback(async () => {
    if (!cafeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/platform/cafes/${cafeId}/tables-wiring`);
      setTables(data?.tables || []);
      setDevices(data?.devices || []);
      const map = {};
      (data?.tables || []).forEach((t) => {
        map[t.id] = {
          device_id: t.device_id || "",
          relay_index: t.relay_index == null ? "" : String(t.relay_index),
        };
      });
      setWiring(map);
    } catch (e) {
      notifyApiError(toast, e);
    } finally {
      setLoading(false);
    }
  }, [cafeId]);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  if (!enabled) return null;

  const saveWiring = async (tableId, device_id, relay_index) => {
    setWireSaving((s) => ({ ...s, [tableId]: true }));
    try {
      await api.patch(`/platform/cafes/${cafeId}/tables/${tableId}/wiring`, {
        device_id: device_id || null,
        relay_index: relay_index === "" ? null : Number(relay_index),
      });
      setWiring((m) => ({
        ...m,
        [tableId]: { device_id: device_id || "", relay_index: String(relay_index ?? "") },
      }));
      toast.success("Wiring saved");
    } catch (err) {
      notifyApiError(toast, err);
    } finally {
      setWireSaving((s) => ({ ...s, [tableId]: false }));
    }
  };

  const testLight = async (tableId, on, tableName) => {
    setTesting((s) => ({ ...s, [tableId]: true }));
    try {
      await api.post(`/platform/cafes/${cafeId}/tables/${tableId}/test-light`, { on });
      toast.success(`${tableName} → ${on ? "ON" : "OFF"}`);
    } catch (err) {
      notifyApiError(toast, err);
    } finally {
      setTesting((s) => ({ ...s, [tableId]: false }));
    }
  };

  return (
    <div
      data-testid="platform-light-wiring-panel"
      className="mt-4 bg-black/40 border border-white/10 rounded-lg"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-white/5 rounded-lg transition-colors"
        data-testid="platform-light-wiring-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Lightbulb className="w-4 h-4 text-amber-300 shrink-0" />
          <div className="text-left min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-200 font-black">
              Table Light Wiring
            </div>
            <div className="text-[10px] text-gray-500 truncate">
              {devices.length} board{devices.length === 1 ? "" : "s"} · {tables.length} table{tables.length === 1 ? "" : "s"} · auto ON on session start
            </div>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>

      {open && (
        <div className="border-t border-white/10 p-3">
          {loading ? (
            <div className="text-center text-gray-500 text-xs py-4">Loading…</div>
          ) : devices.length === 0 ? (
            <div className="text-center text-gray-500 text-xs py-4">
              No boards provisioned yet. Add one under Hardware Automation above.
            </div>
          ) : tables.length === 0 ? (
            <div className="text-center text-gray-500 text-xs py-4">
              No tables yet — add tables to this cafe first.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {tables.map((t) => {
                const w = wiring[t.id] || { device_id: "", relay_index: "" };
                const dev = devices.find((d) => d.id === w.device_id);
                const ready = !!w.device_id && w.relay_index !== "";
                return (
                  <div
                    key={`wire-${t.id}`}
                    data-testid={`platform-wire-card-${t.id}`}
                    className="bg-black/30 border border-white/10 rounded-md p-2 flex flex-wrap items-center gap-2"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black whitespace-nowrap w-14 shrink-0">
                      {t.name}
                    </div>
                    <select
                      data-testid={`platform-wire-board-${t.id}`}
                      value={w.device_id}
                      disabled={wireSaving[t.id]}
                      onChange={(e) => {
                        const newDev = e.target.value;
                        saveWiring(t.id, newDev, newDev ? 0 : "");
                      }}
                      className="bg-black/40 border border-white/10 text-white text-xs rounded-md py-1 px-2 outline-none focus:border-amber-400/60 min-w-[100px] flex-1"
                    >
                      <option value="">— unwired —</option>
                      {devices.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                    <select
                      data-testid={`platform-wire-channel-${t.id}`}
                      value={w.relay_index}
                      disabled={!w.device_id || wireSaving[t.id]}
                      onChange={(e) => saveWiring(t.id, w.device_id, e.target.value)}
                      className="bg-black/40 border border-white/10 text-white text-xs rounded-md py-1 px-2 outline-none focus:border-amber-400/60 w-16 disabled:opacity-40 shrink-0"
                    >
                      <option value="">ch?</option>
                      {dev &&
                        Array.from({ length: dev.relay_count }).map((_, i) => (
                          <option key={`${t.id}-ch-${i}`} value={i}>
                            ch {i}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      data-testid={`platform-test-on-${t.id}`}
                      disabled={!ready || testing[t.id]}
                      onClick={() => testLight(t.id, true, t.name)}
                      className="px-2 py-1 rounded-md text-[10px] uppercase tracking-widest font-black bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-30 inline-flex items-center gap-1"
                      title="Test relay ON"
                    >
                      <Zap className="w-3 h-3" /> On
                    </button>
                    <button
                      type="button"
                      data-testid={`platform-test-off-${t.id}`}
                      disabled={!ready || testing[t.id]}
                      onClick={() => testLight(t.id, false, t.name)}
                      className="px-2 py-1 rounded-md text-[10px] uppercase tracking-widest font-black bg-gray-500/15 border border-gray-400/40 text-gray-300 hover:bg-gray-500/25 disabled:opacity-30 inline-flex items-center gap-1"
                      title="Test relay OFF"
                    >
                      <Power className="w-3 h-3" /> Off
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
